import { useMemo, useState } from "react";
import {
  GoogleAuthProvider,
  TotpMultiFactorGenerator,
  getMultiFactorResolver,
  linkWithPopup,
  multiFactor,
  sendEmailVerification,
  signInWithPopup,
  type MultiFactorError,
  type MultiFactorResolver,
  type TotpSecret,
  type User
} from "firebase/auth";
import { FirebaseError } from "firebase/app";
import { auth } from "./firebase";

const googleProvider = new GoogleAuthProvider();
googleProvider.setCustomParameters({ prompt: "select_account" });

export function mfaResolverFromError(error: unknown): MultiFactorResolver | null {
  if (error instanceof FirebaseError && error.code === "auth/multi-factor-auth-required") {
    return getMultiFactorResolver(auth, error as MultiFactorError);
  }
  return null;
}

export async function signInWithGoogle() {
  return signInWithPopup(auth, googleProvider);
}

export function hasTotpMfa(user: User | null) {
  return Boolean(user && multiFactor(user).enrolledFactors.some((factor) => factor.factorId === TotpMultiFactorGenerator.FACTOR_ID));
}

export async function finishTotpSignIn(resolver: MultiFactorResolver, code: string) {
  const hint = resolver.hints.find((factor) => factor.factorId === TotpMultiFactorGenerator.FACTOR_ID);
  if (!hint) throw new Error("This account does not have a supported authenticator.");
  return resolver.resolveSignIn(TotpMultiFactorGenerator.assertionForSignIn(hint.uid, code.trim()));
}

function readableSecurityError(error: unknown) {
  if (!(error instanceof FirebaseError)) return "That security change could not be completed.";
  if (error.code === "auth/requires-recent-login") return "Please sign out, sign in again, and retry this security change.";
  if (error.code === "auth/provider-already-linked") return "Google is already connected to this account.";
  if (error.code === "auth/credential-already-in-use") return "That Google account is already connected to another FamDashFun account.";
  if (error.code === "auth/invalid-verification-code") return "That six-digit code was not accepted. Wait for a new code and try again.";
  if (error.code === "auth/operation-not-allowed") return "This option is ready in FamDashFun, but it still needs to be enabled in Firebase.";
  return error.message || "That security change could not be completed.";
}

export function AccountSecurityPanel({ user }: { user: User }) {
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");
  const [totpSecret, setTotpSecret] = useState<TotpSecret | null>(null);
  const [code, setCode] = useState("");
  const [refreshKey, setRefreshKey] = useState(0);
  const googleLinked = user.providerData.some((provider) => provider.providerId === GoogleAuthProvider.PROVIDER_ID);
  const enrolledFactors = useMemo(() => multiFactor(user).enrolledFactors, [user, refreshKey]);
  const totpEnrolled = enrolledFactors.some((factor) => factor.factorId === TotpMultiFactorGenerator.FACTOR_ID);

  async function connectGoogle() {
    setBusy(true); setError(""); setNotice("");
    try {
      await linkWithPopup(user, googleProvider);
      setNotice("Google sign-in is now connected to this same FamDashFun account.");
      setRefreshKey((value) => value + 1);
    } catch (nextError) { setError(readableSecurityError(nextError)); }
    finally { setBusy(false); }
  }

  async function verifyEmail() {
    setBusy(true); setError(""); setNotice("");
    try {
      await sendEmailVerification(user);
      setNotice(`Verification email sent to ${user.email}. Open it, then return here and refresh the page.`);
    } catch (nextError) { setError(readableSecurityError(nextError)); }
    finally { setBusy(false); }
  }

  async function beginTotp() {
    setBusy(true); setError(""); setNotice("");
    try {
      if (!user.emailVerified) {
        setError("Verify your email before setting up an authenticator.");
        return;
      }
      const session = await multiFactor(user).getSession();
      setTotpSecret(await TotpMultiFactorGenerator.generateSecret(session));
      setNotice("Add the setup key to your authenticator, then enter its six-digit code.");
    } catch (nextError) { setError(readableSecurityError(nextError)); }
    finally { setBusy(false); }
  }

  async function confirmTotp() {
    if (!totpSecret || code.trim().length !== 6) return;
    setBusy(true); setError(""); setNotice("");
    try {
      await multiFactor(user).enroll(TotpMultiFactorGenerator.assertionForEnrollment(totpSecret, code.trim()), "Authenticator app");
      setTotpSecret(null); setCode("");
      setRefreshKey((value) => value + 1);
      setNotice("Authenticator MFA is on. Future logins will require a six-digit code.");
    } catch (nextError) { setError(readableSecurityError(nextError)); }
    finally { setBusy(false); }
  }

  async function removeTotp() {
    const factor = enrolledFactors.find((item) => item.factorId === TotpMultiFactorGenerator.FACTOR_ID);
    if (!factor || !window.confirm("Remove authenticator MFA from this account?")) return;
    setBusy(true); setError(""); setNotice("");
    try {
      await multiFactor(user).unenroll(factor);
      setRefreshKey((value) => value + 1);
      setNotice("Authenticator MFA was removed.");
    } catch (nextError) { setError(readableSecurityError(nextError)); }
    finally { setBusy(false); }
  }

  const authenticatorUri = totpSecret?.generateQrCodeUrl(user.email || "family-member", "FamDashFun");

  return (
    <div className="account-security">
      <div className="security-heading">
        <div><strong>Sign-in security</strong><small>Protect this individual account and any finances it connects.</small></div>
        <span className={totpEnrolled ? "security-status on" : "security-status"}>{totpEnrolled ? "MFA on" : "MFA off"}</span>
      </div>
      <div className="security-row">
        <div><strong>Google sign-in</strong><small>{googleLinked ? "Connected to this account" : "Link Google without changing your dashboard data"}</small></div>
        <button type="button" disabled={busy || googleLinked} onClick={connectGoogle}>{googleLinked ? "Connected" : "Connect Google"}</button>
      </div>
      {!user.emailVerified && <div className="security-row">
        <div><strong>Verify email</strong><small>Firebase requires a verified email before MFA enrollment.</small></div>
        <button type="button" disabled={busy} onClick={verifyEmail}>Send verification</button>
      </div>}
      <div className="security-row">
        <div><strong>Authenticator MFA</strong><small>Required before this account can connect or open financial data.</small></div>
        {!totpEnrolled && !totpSecret && <button type="button" disabled={busy || !user.emailVerified} onClick={beginTotp}>Set up</button>}
        {totpEnrolled && <button type="button" className="security-remove" disabled={busy} onClick={removeTotp}>Remove</button>}
      </div>
      {totpSecret && <div className="totp-setup">
        <p><strong>1. Add FamDashFun to your authenticator app.</strong></p>
        {authenticatorUri && <a className="auth-submit totp-open-link" href={authenticatorUri}>Open authenticator app</a>}
        <label>Manual setup key<input value={totpSecret.secretKey} readOnly onFocus={(event) => event.currentTarget.select()} /></label>
        <p><strong>2. Enter the current six-digit code.</strong></p>
        <div className="totp-code-row">
          <input aria-label="Six-digit authenticator code" inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={code} onChange={(event) => setCode(event.target.value.replace(/\D/g, "").slice(0, 6))} />
          <button type="button" disabled={busy || code.length !== 6} onClick={confirmTotp}>{busy ? "Checking…" : "Turn on MFA"}</button>
        </div>
        <button type="button" className="auth-link" onClick={() => { setTotpSecret(null); setCode(""); }}>Cancel setup</button>
      </div>}
      {error && <p className="auth-error" role="alert">{error}</p>}
      {notice && <p className="auth-success" role="status">{notice}</p>}
      <p className="security-note">Financial access is capability-based: any owner, parent/admin, adult, or teen who connects finances must use MFA. A household role never automatically shares someone’s financial data.</p>
    </div>
  );
}

